import type { JSX } from 'react';
import {
  Ban,
  CheckCircle2,
  Clock,
  EyeOff,
  MessageSquare,
  MinusCircle,
  Send,
  UserCheck,
} from 'lucide-react';
import { Pill, type PillTone } from '@/components/ui/pill';
import type { CreatorOutreachFields, LeadDoneReason, OutreachState, SkipBreakdown } from '../api/types';

/**
 * One place that decides how funnel state is worded and coloured.
 *
 * The folder table, the live search results and the Creators tab all render
 * this, so a creator can't read one way in one view and another way in the
 * next, which is what the old two-surface, three-label badge did.
 *
 * Words follow the outreacher flow (Found, Messaged, Replied, Joined,
 * Posted): a logged invite is a "Reply logged", and a creator already on
 * clipping is "On platform" (they joined without anyone's outreach).
 *
 * Uses the portal's `<Pill>` primitive: per DESIGN.md, state is a Pill and
 * metadata is a Tag, and funnel position is state.
 */

interface StateStyle {
  label: string;
  tone: PillTone;
  icon: typeof Clock;
  /** Shown on hover; the short label has to fit a table cell. */
  hint: string;
}

const STATE_STYLES: Record<OutreachState, StateStyle> = {
  none: {
    label: 'No outreach recorded',
    tone: 'muted',
    icon: MinusCircle,
    hint: 'No outreach has been recorded for this creator yet.',
  },
  dismissed: {
    label: 'Removed from queue',
    tone: 'rejected',
    icon: Ban,
    hint: 'An outreacher removed this creator from the lead queue.',
  },
  prospect: {
    label: 'In lead queue',
    tone: 'pending',
    icon: Clock,
    hint: 'In the lead queue. Nobody has messaged them yet.',
  },
  // Legacy state from when leads could be reserved. Leads now stay open to
  // everyone until a reply is logged; kept as its own word so the filter list
  // doesn't show two identical options.
  claimed: {
    label: 'Picked up',
    tone: 'info',
    icon: UserCheck,
    hint: 'Picked up from the lead queue. Still open to everyone until a reply is logged.',
  },
  contacted: {
    label: 'Messaged',
    tone: 'info',
    icon: MessageSquare,
    hint: "An outreacher messaged this creator. No reply has been logged yet.",
  },
  invited: {
    label: 'Reply logged',
    tone: 'submitted',
    icon: Send,
    hint: 'The creator replied asking to join, and an outreacher logged it.',
  },
  joined: {
    label: 'On platform',
    tone: 'success',
    icon: CheckCircle2,
    hint: 'Already on the clipping platform. No outreach needed.',
  },
};

export function outreachStateLabel(state: OutreachState): string {
  return STATE_STYLES[state]?.label ?? state;
}

export const OUTREACH_STATE_ORDER: OutreachState[] = [
  'none',
  'prospect',
  'claimed',
  'contacted',
  'invited',
  'joined',
  'dismissed',
];

interface Props {
  /** Any row carrying the registry's mirrored status fields. */
  creator: Partial<CreatorOutreachFields> & { outreachState?: OutreachState };
  /** Render nothing rather than a "No outreach recorded" pill (keeps dense tables calm). */
  hideNone?: boolean;
}

export function OutreachStatusBadge({ creator, hideNone }: Props): JSX.Element | null {
  const state = creator.outreachState ?? 'none';
  if (state === 'none' && hideNone) return null;

  const style = STATE_STYLES[state] ?? STATE_STYLES.none;
  // Who picked a legacy reserved lead up goes in the tooltip, not the label:
  // the lead is open to everyone, so the name is context, not ownership.
  const label = style.label;
  const hint =
    state === 'claimed' && creator.outreachClaimedByName
      ? `${style.hint} Picked up by ${creator.outreachClaimedByName}.`
      : style.hint;

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span title={hint}>
        <Pill tone={style.tone} icon={style.icon}>
          {label}
        </Pill>
      </span>
      {creator.suppressedAt && (
        <span title="Suppressed. Excluded from all future searches.">
          <Pill tone="rejected" icon={EyeOff}>
            Suppressed
          </Pill>
        </span>
      )}
    </span>
  );
}

/**
 * Why a lead counts as done for folder completion (contract §3). Worded from
 * the folder's point of view; "messaged, no reply" is done for the folder
 * only and never stops the creator being outreached again.
 */
const DONE_REASON_LABELS: Record<LeadDoneReason, string> = {
  logged: 'Reply logged',
  joined: 'On platform',
  dismissed: 'Removed from queue',
  suppressed: 'Suppressed',
  irrelevant: 'Marked irrelevant',
  complete: 'Marked complete',
  wrong_fit: 'Wrong fit (outreacher)',
  messaged_no_reply: 'Messaged, no reply',
};

export function doneReasonLabel(reason: LeadDoneReason | null | undefined): string | null {
  return reason ? (DONE_REASON_LABELS[reason] ?? reason) : null;
}

/** Filter options for the leads table's "Done" filter. */
export const DONE_FILTER_OPTIONS = [
  { value: 'no', label: 'Open' },
  { value: 'yes', label: 'Done' },
] as const;

/**
 * "180 skipped: 165 already in another folder, 12 already in outreach, 3
 * already on the platform." Without this, a search that correctly filtered
 * out everything you already had just looked like it failed.
 */
/** Why creators were skipped. Mirrors SKIP_LABELS in the discovery service. */
const SKIP_LABELS: Record<keyof SkipBreakdown, string> = {
  known: 'already in another folder',
  outreach: 'already in outreach',
  joined: 'already on the platform',
  suppressed: 'suppressed',
  offTarget: 'wrong account type',
  offTopic: 'off-topic',
  faceless: 'not face creators',
  hasFace: 'face creators',
  unverified: "couldn't be checked on camera",
  audienceMiss: 'audience mostly elsewhere',
  audienceUnmeasured: 'too few comments to measure audience',
  languageMiss: 'post in another language',
  languageUnmeasured: 'too little text to tell the language',
  countryMiss: 'based in another country',
  countryUnmeasured: 'location unknown',
  notPage: 'personal accounts, not pages',
  notPerson: 'pages, not people',
  fewFollowers: 'under the follower minimum',
  tooManyFollowers: 'over the follower maximum',
  lowViews: 'under the avg views minimum',
  highViews: 'over the avg views maximum',
  videoCount: 'outside the post count range',
  inactive: "haven't posted recently",
  lowEngagement: 'engagement too low',
  lowPostRate: "don't post often enough",
  notVerified: 'not verified',
  noBioLink: 'no bio link',
  bioMismatch: 'no bio keyword match',
  emailMismatch: "email didn't match the filter",
  accountKind: 'wrong Instagram account kind',
  profileUnavailable: 'profile unavailable',
};

export function SkipSummary({
  skipped,
  breakdown,
}: {
  skipped: number;
  breakdown: SkipBreakdown | null;
}): JSX.Element | null {
  if (!skipped) return null;
  // Largest reason first, so the filter doing most of the rejecting leads.
  const parts = (Object.entries(breakdown ?? {}) as [keyof SkipBreakdown, number | undefined][])
    .filter(([k, n]) => (n ?? 0) > 0 && SKIP_LABELS[k])
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
    .map(([k, n]) => `${n} ${SKIP_LABELS[k]}`);

  return (
    <div className="rounded-lg border-l-2 border-info bg-info/10 px-3 py-2 text-sm">
      <strong className="font-semibold">{skipped}</strong> skipped
      {parts.length > 0 && <span className="text-muted-foreground">: {parts.join(', ')}</span>}
    </div>
  );
}
